import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { CreateFinancialBookPayload, FinanceApiService, FinancialBook, UpdateFinancialBookPayload } from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-control-center-books',
  imports: [AinPageComponent, InfoHintComponent, TableModule, RouterLink, FormsModule, ButtonModule, InputTextModule, SelectModule, CurrencySelectorComponent],
  template: `
    <ain-page [title]="t('finance.books.title')" [description]="t('finance.books.description')">
      <div class="toolbar">
        <a class="back-link" [routerLink]="['/financial/control-center']">{{ t('finance.books.backToControlCenter') }}</a>
        <button pButton type="button" [label]="t('finance.books.refresh')" severity="secondary" (click)="load()"></button>
      </div>

      <div class="layout">
        <div class="card">
          <div class="filters">
            <input pInputText type="text" [(ngModel)]="searchCode" [placeholder]="t('finance.books.searchCode')" />
            <input pInputText type="text" [(ngModel)]="searchName" [placeholder]="t('finance.books.searchName')" />
            <button pButton type="button" [label]="t('finance.books.search')" (click)="applyFilters()"></button>
          </div>
          <div class="actions">
            <button pButton type="button" [label]="t('finance.books.addNew')" (click)="startCreate()"></button>
            <button pButton type="button" [label]="t('finance.books.editSelected')" severity="secondary" [disabled]="!selected()" (click)="startEdit()"></button>
          </div>

          <p-table [value]="filteredBooks()" selectionMode="single" [selection]="selected()" (selectionChange)="onSelect($event)" dataKey="id">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('finance.books.fields.code') }}</th>
                <th>{{ t('finance.books.fields.name') }}</th>
                <th>{{ t('finance.books.fields.type') }}</th>
                <th>{{ t('finance.books.fields.currencyCode') }}</th>
                <th>{{ t('finance.books.fields.isActive') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr [pSelectableRow]="row">
                <td>{{ row.code }}</td>
                <td>{{ row.name }}</td>
                <td>{{ t('finance.books.types.' + row.type.toLowerCase()) }}</td>
                <td>{{ row.currencyCode }}</td>
                <td>{{ row.isActive ? t('common.yes') : t('common.no') }}</td>
              </tr>
            </ng-template>
          </p-table>

          @if (errorMessage()) {
            <p class="message error">{{ errorMessage() }}</p>
          }
          @if (successMessage()) {
            <p class="message success">{{ successMessage() }}</p>
          }
        </div>
      </div>

      @if (dialogOpen()) {
        <div class="modal-backdrop" (click)="cancel()">
          <div class="modal-card" (click)="$event.stopPropagation()">
            <h3>{{ mode() === 'edit' ? t('finance.books.form.editTitle') : t('finance.books.form.createTitle') }}</h3>
            @if (mode() === 'create') {
              <ain-info-hint
                [titleKey]="'finance.books.standardChartHint.title'"
                [descriptionKey]="'finance.books.standardChartHint.description'"
                [bulletKeys]="['finance.books.standardChartHint.bullets.template', 'finance.books.standardChartHint.bullets.optional']" />
            }
            <div class="form">
              <label>
                <span>{{ t('finance.books.fields.code') }}</span>
                <input pInputText type="text" [(ngModel)]="form.code" />
              </label>
              <label>
                <span>{{ t('finance.books.fields.name') }}</span>
                <input pInputText type="text" [(ngModel)]="form.name" />
              </label>
              <label>
                <span>{{ t('finance.books.fields.type') }}</span>
                <p-select
                  class="field-select"
                  [options]="bookTypes"
                  optionValue="value"
                  [(ngModel)]="form.type"
                  appendTo="self">
                  <ng-template pTemplate="selectedItem" let-item>
                    {{ item ? t(item.labelKey) : '-' }}
                  </ng-template>
                  <ng-template pTemplate="item" let-item>
                    {{ t(item.labelKey) }}
                  </ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('finance.books.fields.currencyCode') }}</span>
                <ain-currency-selector [(ngModel)]="form.currencyCode" appendTo="self"></ain-currency-selector>
              </label>
              <label>
                <span>{{ t('finance.books.fields.legalEntityId') }}</span>
                <input pInputText type="text" [(ngModel)]="form.legalEntityId" />
              </label>
              @if (mode() === 'create') {
                <label class="check full">
                  <input type="checkbox" [(ngModel)]="seedStandardChartOfAccounts" />
                  <span>{{ t('finance.books.fields.seedStandardChartOfAccounts') }}</span>
                </label>
              } @else {
                <label class="check">
                  <input type="checkbox" [(ngModel)]="form.isActive" />
                  <span>{{ t('finance.books.fields.isActive') }}</span>
                </label>
              }
            </div>

            <div class="actions">
              <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
              <button pButton type="button" [label]="t('common.cancel')" severity="secondary" (click)="cancel()"></button>
            </div>
            @if (errorMessage()) {
              <p class="message error">{{ errorMessage() }}</p>
            }
          </div>
        </div>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; gap: 10px; justify-content: space-between; align-items: center; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .layout { display: grid; grid-template-columns: 1fr; gap: 12px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; display: grid; gap: 10px; }
    .filters, .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .form { display: grid; gap: 10px; }
    label { display: grid; gap: 6px; font-size: 13px; color: #334155; }
    .field-select { width: 100%; }
    input[type='text'] { height: 38px; border: 1px solid #d1d5db; border-radius: 8px; padding: 0 10px; }
    .check { display: flex; align-items: center; gap: 8px; }
    .message { margin: 0; font-size: 13px; }
    .error { color: #b91c1c; }
    .success { color: #166534; }
    .modal-backdrop {
      position: fixed;
      inset: 0;
      background: rgb(15 23 42 / 48%);
      display: grid;
      place-items: center;
      z-index: 1200;
      padding: 16px;
    }
    .modal-card {
      width: min(560px, 100%);
      background: #fff;
      border-radius: 12px;
      border: 1px solid #d1d5db;
      padding: 14px;
      display: grid;
      gap: 10px;
      max-height: calc(100vh - 32px);
      overflow: auto;
    }
    :host ::ng-deep .modal-card .p-select-overlay { z-index: 1601 !important; }
    :host ::ng-deep .modal-card .p-overlay { z-index: 1601 !important; }
  `]
})
export class FinanceControlCenterBooksComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);

  readonly books = signal<FinancialBook[]>([]);
  readonly filteredBooks = signal<FinancialBook[]>([]);
  readonly selected = signal<FinancialBook | null>(null);
  readonly dialogOpen = signal(false);
  readonly mode = signal<'create' | 'edit'>('create');
  readonly errorMessage = signal('');
  readonly successMessage = signal('');

  searchCode = '';
  searchName = '';
  form: UpdateFinancialBookPayload = this.createEmptyForm();
  seedStandardChartOfAccounts = true;

  readonly bookTypes = [
    { value: 'Management', labelKey: 'finance.books.types.management' },
    { value: 'Statutory', labelKey: 'finance.books.types.statutory' },
    { value: 'Tax', labelKey: 'finance.books.types.tax' },
    { value: 'Cash', labelKey: 'finance.books.types.cash' },
    { value: 'Government', labelKey: 'finance.books.types.government' },
    { value: 'TenantDefined', labelKey: 'finance.books.types.tenantdefined' }
  ];

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.api.listBooks().subscribe({
      next: rows => {
        this.books.set(rows);
        this.applyFilters();
      },
      error: () => this.errorMessage.set(this.t('finance.books.messages.loadFailed'))
    });
  }

  applyFilters(): void {
    const code = this.searchCode.trim().toLowerCase();
    const name = this.searchName.trim().toLowerCase();
    this.filteredBooks.set(
      this.books().filter(item =>
        (!code || item.code.toLowerCase().includes(code)) &&
        (!name || item.name.toLowerCase().includes(name)))
    );
  }

  onSelect(row: FinancialBook | null): void {
    this.selected.set(row);
    if (!row) {
      return;
    }

    this.form = {
      legalEntityId: row.legalEntityId ?? '',
      code: row.code,
      name: row.name,
      type: row.type,
      currencyCode: row.currencyCode,
      isActive: row.isActive
    };
  }

  startCreate(): void {
    this.mode.set('create');
    this.selected.set(null);
    this.errorMessage.set('');
    this.form = this.createEmptyForm();
    this.seedStandardChartOfAccounts = true;
    this.dialogOpen.set(true);
  }

  startEdit(): void {
    const row = this.selected();
    if (!row) {
      this.errorMessage.set(this.t('finance.books.messages.noSelection'));
      return;
    }

    this.mode.set('edit');
    this.onSelect(row);
    this.errorMessage.set('');
    this.dialogOpen.set(true);
  }

  save(): void {
    const code = this.form.code.trim();
    const name = this.form.name.trim();
    if (!code || !name) {
      this.errorMessage.set(this.t('finance.books.messages.invalidForm'));
      return;
    }

    this.errorMessage.set('');
    this.successMessage.set('');
    if (this.mode() === 'edit' && this.selected()) {
      const selected = this.selected()!;
      this.api.updateBook(selected.id, {
        legalEntityId: this.normalizeGuid(this.form.legalEntityId),
        code,
        name,
        type: this.form.type,
        currencyCode: this.form.currencyCode || 'IRR',
        isActive: this.form.isActive
      }).subscribe({
        next: row => {
          this.successMessage.set(this.t('finance.books.messages.saved'));
          this.replaceRow(row);
          this.selected.set(row);
          this.dialogOpen.set(false);
        },
        error: err => this.errorMessage.set(this.resolveSaveError(err))
      });
      return;
    }

    const payload: CreateFinancialBookPayload = {
      legalEntityId: this.normalizeGuid(this.form.legalEntityId),
      code,
      name,
      type: this.form.type,
      currencyCode: this.form.currencyCode || 'IRR',
      seedStandardChartOfAccounts: this.seedStandardChartOfAccounts
    };
    this.api.createBook(payload).subscribe({
      next: row => {
        this.successMessage.set(this.t('finance.books.messages.created'));
        this.books.update(items => [row, ...items]);
        this.applyFilters();
        this.mode.set('edit');
        this.selected.set(row);
        this.onSelect(row);
        this.dialogOpen.set(false);
      },
      error: err => this.errorMessage.set(this.resolveSaveError(err))
    });
  }

  cancel(): void {
    this.dialogOpen.set(false);
    if (this.selected()) {
      this.onSelect(this.selected());
      return;
    }
    this.form = this.createEmptyForm();
    this.errorMessage.set('');
  }

  private replaceRow(updated: FinancialBook): void {
    this.books.update(items => items.map(item => item.id === updated.id ? updated : item));
    this.applyFilters();
  }

  private normalizeGuid(value?: string): string | undefined {
    const normalized = value?.trim();
    return normalized ? normalized : undefined;
  }

  private resolveSaveError(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error as { Error?: string; error?: string } | null;
      const code = body?.Error ?? body?.error;
      if (code === 'standard_chart_template_book_not_found') {
        return this.t('finance.books.messages.templateBookNotFound');
      }
    }
    return this.t('finance.books.messages.saveFailed');
  }

  private createEmptyForm(): UpdateFinancialBookPayload {
    return {
      legalEntityId: '',
      code: '',
      name: '',
      type: 'Management',
      currencyCode: 'IRR',
      isActive: true
    };
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
